/**
 * InteractiveSignPost — Shared reusable component for rendering
 * dynamic post type SVG designs with interactive slot areas.
 *
 * Used by both:
 *   - PostTypes.jsx  (post type creation / editing)
 *   - PostTypeSignConfiguration.tsx  (installation page)
 *
 * Single source of truth — no duplication.
 */
import React from "react";

/* ────────────────────── Types ────────────────────── */

export type SlotPosition = {
  key: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  labelY: number;
  labelX?: number;
  fontSize?: number;
};

export type SlotData = {
  products: { id: string; name?: string; sku?: string | null }[];
  brands: { id: string; name?: string }[];
  categories: { id: string; name?: string }[];
};

/**
 * A slot blueprint coming from templates or the API.
 * Supports rect_viewbox (viewBox coords), rect_norm (0-1), and zone.
 * Backend metadata: display_mode, default_visible, allow_remove, group, required.
 */
export type SlotBlueprint = {
  key: string;
  label?: string;
  rect_viewbox?: { x?: number; y?: number; w?: number; h?: number };
  rect_norm?: { x?: number; y?: number; w?: number; h?: number };
  zone?: { x?: number; y?: number; w?: number; h?: number };
  ui?: { label_x?: number; label_y?: number; label_cx?: number; label_cy?: number; font_size?: number };
  display_mode?: "always" | "toggle";
  default_visible?: boolean;
  allow_remove?: boolean;
  group?: string;
  required?: boolean;
};

export interface InteractiveSignPostProps {
  /** Illustration/template id (for backwards compatibility) */
  panelSlots: string;
  /** Data for each slot keyed by slot key */
  panelData: Record<string, SlotData>;
  /** Click handler: (slotKey, slotLabel) => void */
  onPanelClick: (slotKey: string, slotLabel: string) => void;
  /** Slot blueprints from API template schema.illustration.slots (dynamic data only) */
  customSlots?: SlotBlueprint[] | null;
  /** Custom viewBox dimensions from template schema */
  viewBoxDimensions?: { width: number; height: number } | null;
  /** Illustration SVG URL from template assets — rendered as background when provided */
  illustrationSvgUrl?: string | null;
  /** Currently hovered slot key (managed externally) */
  hoveredSlot?: string | null;
  /** Hover state handler (managed externally) */
  onSlotHover?: (key: string | null) => void;
  /** Message shown when no slots exist */
  emptyMessage?: string;
  /** Product image URLs keyed by slot key — when present, renders image in the slot */
  slotImageUrls?: Record<string, string>;
  /** Slot keys where a product is assigned but there is no preview image — dashed box + "No image" */
  slotNoImageKeys?: string[];
  /** Slot keys that have a product assigned — not clickable (no "Add panel" again) */
  disabledSlotKeys?: string[];
  /** Visibility per slot key (toggle slots can be hidden; omit or true = visible). */
  visibleSlots?: Record<string, boolean>;
  /** Explicit view-mode flag (read-only: no add/remove, hide hidden slots) */
  isViewMode?: boolean;
  /** Called when user clicks collapsed "+ Add rider" to show the slot. */
  onAddSlot?: (slotKey: string) => void;
  /** Called when user clicks delete on a removable slot to hide and clear it. */
  onRemoveSlot?: (slotKey: string) => void;
}

/* ────────────────────── Constants ────────────────────── */

export const DEFAULT_VIEWBOX = { width: 200, height: 240 };

/* ────────────────────── Helpers ────────────────────── */

export const parseViewBoxString = (
  viewBox: string
): { width: number; height: number } => {
  if (!viewBox) return { ...DEFAULT_VIEWBOX };
  const parts = viewBox
    .split(/\s+/)
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  if (parts.length >= 4) {
    return { width: parts[2], height: parts[3] };
  }
  return { ...DEFAULT_VIEWBOX };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getTemplateViewBoxDimensions = (
  template: any
): { width: number; height: number } => {
  if (!template || !template.schema) return { ...DEFAULT_VIEWBOX };
  const defaults = template.schema.defaults || {};
  if (defaults.viewBox_w && defaults.viewBox_h) {
    return { width: defaults.viewBox_w, height: defaults.viewBox_h };
  }
  if (defaults.viewBox) {
    return parseViewBoxString(defaults.viewBox);
  }
  if (template.schema.illustration?.viewBox) {
    return parseViewBoxString(template.schema.illustration.viewBox);
  }
  return { ...DEFAULT_VIEWBOX };
};

/** Normalize a slot blueprint into pixel-based SlotPosition.
 * API schema supports: rect_viewbox (viewBox coords), rect_norm (0-1), zone.
 * ui: label_cy, label_cx (or label_y, label_x).
 */
export const normalizeSlot = (
  slot: SlotBlueprint,
  viewBox: { width: number; height: number }
): SlotPosition | null => {
  if (!slot || !slot.key) return null;
  let x: number, y: number, w: number, h: number;
  const rv = slot.rect_viewbox;
  const rn = slot.rect_norm;
  const z = slot.zone;
  if (rv && typeof rv.x === "number" && typeof rv.w === "number") {
    x = rv.x;
    y = typeof rv.y === "number" ? rv.y : 0;
    w = rv.w;
    h = typeof rv.h === "number" ? rv.h : viewBox.height * 0.2;
  } else if (z && typeof z.x === "number") {
    x = z.x * viewBox.width;
    y = (typeof z.y === "number" ? z.y : 0) * viewBox.height;
    w = (typeof z.w === "number" ? z.w : 0.6) * viewBox.width;
    h = (typeof z.h === "number" ? z.h : 0.2) * viewBox.height;
  } else if (rn && typeof rn.x === "number") {
    x = rn.x * viewBox.width;
    y = (typeof rn.y === "number" ? rn.y : 0) * viewBox.height;
    w = (typeof rn.w === "number" ? rn.w : 0.6) * viewBox.width;
    h = (typeof rn.h === "number" ? rn.h : 0.2) * viewBox.height;
  } else {
    x = 0;
    y = 0;
    w = viewBox.width * 0.6;
    h = viewBox.height * 0.2;
  }
  const ui = slot.ui || {};
  const labelY = ui.label_cy ?? ui.label_y ?? y + h / 2;
  const labelX = ui.label_cx ?? ui.label_x ?? x + w / 2;
  return {
    key: slot.key,
    label: slot.label || "+ Add panel",
    x,
    y,
    w,
    h,
    labelY,
    labelX,
    fontSize: ui.font_size,
  };
};

/* ────────────────────── Component ────────────────────── */

const InteractiveSignPost: React.FC<InteractiveSignPostProps> = ({
  panelSlots,
  panelData,
  onPanelClick,
  customSlots,
  viewBoxDimensions,
  illustrationSvgUrl,
  hoveredSlot,
  onSlotHover,
  emptyMessage,
  slotImageUrls,
  slotNoImageKeys = [],
  disabledSlotKeys = [],
  visibleSlots,
  isViewMode,
  onAddSlot,
  onRemoveSlot,
}) => {
  const viewBox = viewBoxDimensions || DEFAULT_VIEWBOX;
  const slotNoImageSet = React.useMemo(() => new Set(slotNoImageKeys), [slotNoImageKeys]);

  // Slots from API template schema only (no static fallback)
  const slots: SlotPosition[] = Array.isArray(customSlots)
    ? (customSlots.map((s) => normalizeSlot(s, viewBox)).filter(Boolean) as SlotPosition[])
    : [];

  return (
    <svg
      width={viewBox.width}
      height={viewBox.height}
      viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
      fill="none"
      style={{ overflow: "visible" }}
    >
      {/* Illustration SVG from template assets (dynamic) */}
      {illustrationSvgUrl && (
        <image
          href={illustrationSvgUrl}
          x={0}
          y={0}
          width={viewBox.width}
          height={viewBox.height}
          preserveAspectRatio="xMidYMid meet"
        />
      )}

      {/* Slot areas */}
      {slots.map((slot) => {
        const blueprint = customSlots?.find((c) => c.key === slot.key);
        const isVisible = visibleSlots?.[slot.key] ?? true;
        // Backend may allow removing riders but not `main_panel`.
        // UX requirement: still allow clearing the currently selected `main_panel` product via close/X.
        const canRemove = blueprint?.allow_remove === true || slot.key === "main_panel";
        const isToggleSlot = blueprint?.display_mode === "toggle";

        // Hidden slots
        if (!isVisible) {
          const bleed = 2;
          const coverX = slot.x - bleed;
          const coverY = slot.y - bleed;
          const coverW = slot.w + bleed * 2;
          const coverH = slot.h + bleed * 2;
          // View mode: fill with white so slot looks hidden (same feel as add mode)
          if (isViewMode) {
            return (
              <g key={slot.key}>
                <rect
                  x={coverX}
                  y={coverY}
                  width={coverW}
                  height={coverH}
                  rx="6"
                  fill="#ffffff"
                  stroke="none"
                />
              </g>
            );
          }

          // Add mode: hidden toggle slots show the "Add Rider" button, others are effectively always-visible
          if (isToggleSlot && onAddSlot) {
            const addLabel = slot.key === "top_rider" ? "Add Topper" : "Add Rider";
            const btnW = slot.w * 0.7;
            const btnH = Math.max(slot.h * 0.6, 24);
            const btnX = slot.x + (slot.w - btnW) / 2;
            const btnY = slot.y + (slot.h - btnH) / 2;
            const bleed = 2;
            const coverX = slot.x - bleed;
            const coverY = slot.y - bleed;
            const coverW = slot.w + bleed * 2;
            const coverH = slot.h + bleed * 2;
            return (
              <g
                key={slot.key}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSlot?.(slot.key);
                }}
                style={{ cursor: "pointer" }}
              >
                {/* Solid white cover (slightly larger) so illustration dashed border is fully hidden */}
                <rect
                  x={coverX}
                  y={coverY}
                  width={coverW}
                  height={coverH}
                  rx="6"
                  fill="#ffffff"
                  stroke="none"
                />
                <rect
                  x={btnX}
                  y={btnY}
                  width={btnW}
                  height={btnH}
                  rx="8"
                  fill="#ffffff"
                  stroke="#d1d5db"
                  strokeWidth="1"
                />
                <text
                  x={slot.x + slot.w / 2}
                  y={slot.y + slot.h / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="13"
                  fontWeight="500"
                  fill="#2563eb"
                  fontFamily="system-ui"
                >
                  {addLabel}
                </text>
              </g>
            );
          }
        }

        const data =
          panelData[slot.key] || {
            products: [],
            brands: [],
            selectedCategoriesByBrand: {},
            standaloneCategories: [],
          };

        const productsCount = Array.isArray((data as any).products)
          ? (data as any).products.length
          : 0;
        const flatBrands = Array.isArray((data as any).brands) ? (data as any).brands : [];
        const explicitBrandIds = (data as any).explicitlySelectedBrandIds;
        const brandsCount =
          Array.isArray(explicitBrandIds) && explicitBrandIds.length > 0
            ? explicitBrandIds.length
            : flatBrands.length;
        // Backwards-compat: support old `categories` as well as new brand-scoped + standalone
        const legacyCategoriesCount = Array.isArray((data as any).categories)
          ? (data as any).categories.length
          : 0;
        const standaloneCategoriesCount = Array.isArray(
          (data as any).standaloneCategories
        )
          ? (data as any).standaloneCategories.length
          : 0;
        const brandScopedCategoriesCount = (() => {
          const byBrand = (data as any).selectedCategoriesByBrand;
          if (!byBrand || typeof byBrand !== "object") return 0;
          return (Object.values(byBrand) as any[]).reduce((total: number, arr: any) => {
            return total + (Array.isArray(arr) ? arr.length : 0);
          }, 0);
        })();
        const explicitCatByBrand = (data as any).explicitlySelectedCategoryIdsByBrand;
        let explicitCategoryCount = 0;
        if (explicitCatByBrand && typeof explicitCatByBrand === "object") {
          explicitCategoryCount = (Object.values(explicitCatByBrand) as any[]).reduce(
            (t: number, arr: any) => t + (Array.isArray(arr) ? arr.length : 0),
            0,
          );
        }
        const standaloneExplicitCats = (data as any).explicitlySelectedStandaloneCategoryIds;
        if (Array.isArray(standaloneExplicitCats)) {
          explicitCategoryCount += standaloneExplicitCats.length;
        }
        const aggregatedLegacyCategoryCount =
          legacyCategoriesCount + standaloneCategoriesCount + brandScopedCategoriesCount;
        const categoriesCount =
          explicitCategoryCount > 0 ? explicitCategoryCount : aggregatedLegacyCategoryCount;

        const hasSelectAllCategories =
          Array.isArray((data as any).select_all_categories_per_brand) &&
          (data as any).select_all_categories_per_brand.length > 0;

        const hasData =
          productsCount > 0 ||
          brandsCount > 0 ||
          categoriesCount > 0 ||
          !!(data as any).select_all_brands ||
          hasSelectAllCategories;
        const lx = typeof slot.labelX === "number" ? slot.labelX : slot.x + slot.w / 2;
        const isHov = hoveredSlot === slot.key;
        const imageUrl = slotImageUrls?.[slot.key];
        const isDisabled = disabledSlotKeys.includes(slot.key);
        const showNoImage = slotNoImageSet.has(slot.key);

        return (
          <g
            key={slot.key}
            onClick={(e) => {
              e.stopPropagation();
              if (isDisabled) return;
              onPanelClick(slot.key, slot.label);
            }}
            onMouseEnter={onSlotHover && !isDisabled ? () => onSlotHover(slot.key) : undefined}
            onMouseLeave={onSlotHover && !isDisabled ? () => onSlotHover(null) : undefined}
            style={{
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled && !showNoImage ? 0.92 : 1,
            }}
          >
            {/* When illustration is present, use transparent overlay so template's slot + label show through; no duplicate text/rect */}
            {!illustrationSvgUrl && (
              <rect
                x={Math.max(0, slot.x - 4)}
                y={Math.max(0, slot.y - 4)}
                width={slot.w + 8}
                height={slot.h + 8}
                rx="8"
                fill={imageUrl ? "#1a1a1a" : showNoImage ? "#e2e8f0" : isHov ? "rgba(59,130,246,0.06)" : hasData ? "#EFF6FF" : "white"}
              />
            )}
            {/* Slot rect: with illustration only show hover/assign state overlay to avoid double slot design */}
            <rect
              x={slot.x}
              y={slot.y}
              width={slot.w}
              height={slot.h}
              rx="4"
              fill={
                showNoImage
                  ? "#e2e8f0"
                  : illustrationSvgUrl && !imageUrl && !hasData
                    ? isHov
                      ? "rgba(59,130,246,0.08)"
                      : "transparent"
                    : imageUrl
                      ? "#1a1a1a"
                      : isHov
                        ? "rgba(59,130,246,0.06)"
                        : hasData
                          ? "#EFF6FF"
                          : "white"
              }
              stroke={
                showNoImage ? "#94a3b8" : imageUrl ? "#333" : hasData ? "#3B82F6" : isHov ? "#93C5FD" : "#BCBCBC"
              }
              strokeWidth="1.5"
              strokeDasharray={showNoImage ? "6 4" : imageUrl || hasData ? "none" : "6 4"}
              opacity={
                imageUrl
                  ? 0.12
                  : showNoImage
                    ? 1
                    : illustrationSvgUrl && !imageUrl && !hasData
                      ? isHov
                        ? 1
                        : 0
                      : 1
              }
            />

            {/* Product image — when a product is assigned to this slot */}
            {imageUrl ? (
              <>
                <image
                  href={imageUrl}
                  x={slot.x + 1}
                  y={slot.y + 1}
                  width={slot.w - 2}
                  height={slot.h - 2}
                  preserveAspectRatio="xMidYMid slice"
                  clipPath={`inset(0 round 4px)`}
                />
                {/* Subtle border overlay */}
                <rect
                  x={slot.x}
                  y={slot.y}
                  width={slot.w}
                  height={slot.h}
                  rx="4"
                  fill={isHov ? "rgba(59,130,246,0.15)" : "transparent"}
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                />
              </>
            ) : showNoImage ? (
              <text
                x={lx}
                y={slot.y + slot.h / 2}
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize={Math.min(16, Math.max(12, slot.h * 0.32))}
                fontWeight="700"
                fill="#334155"
                opacity={1}
                fontFamily="system-ui, sans-serif"
              >
                No image
              </text>
            ) : (
              <>
                {/* CSS hover fallback when hoveredSlot is NOT managed externally */}
                {!onSlotHover && (
                  <rect
                    x={slot.x}
                    y={slot.y}
                    width={slot.w}
                    height={slot.h}
                    rx="4"
                    fill="transparent"
                    className="ph"
                  />
                )}

                {hasData ? (
                  <>
                    <text
                      x={lx}
                      y={slot.y + slot.h / 2 - 6}
                      textAnchor="middle"
                      fontSize="10"
                      fontWeight="600"
                      fill="#1D4ED8"
                      fontFamily="system-ui"
                    >
                      {[
                        brandsCount > 0
                          ? `${brandsCount} brand${brandsCount > 1 ? "s" : ""}`
                          : "",
                        (legacyCategoriesCount + standaloneCategoriesCount + brandScopedCategoriesCount) > 0
                          ? `${legacyCategoriesCount + standaloneCategoriesCount + brandScopedCategoriesCount} categor${
                              legacyCategoriesCount + standaloneCategoriesCount + brandScopedCategoriesCount > 1
                                ? "ies"
                                : "y"
                            }`
                          : "",
                        productsCount > 0
                          ? `${productsCount} product${productsCount > 1 ? "s" : ""}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </text>
                    <text
                      x={lx}
                      y={slot.y + slot.h / 2 + 8}
                      textAnchor="middle"
                      fontSize="8.5"
                      fill="#60A5FA"
                      fontFamily="system-ui"
                    >
                      Click to edit
                    </text>
                  </>
                ) : (
                  /* When illustrationSvgUrl is set, template already has slot label — avoid double text on hover */
                  !illustrationSvgUrl && (
                    <text
                      x={lx}
                      y={slot.labelY}
                      textAnchor="middle"
                      fontSize={slot.fontSize || 12}
                      fill="#AAA"
                      fontFamily="system-ui"
                    >
                      {slot.label}
                    </text>
                  )
                )}
              </>
            )}
            {/* Remove button: outside slot on the right, close/cross icon (removable slots only) */}
            {canRemove && onRemoveSlot && (slot.key === "main_panel" ? isDisabled : true) && (() => {
              const gap = 12;
              const btnSize = 25;
              const btnX = slot.x + slot.w + gap;
              const btnY = slot.y + slot.h / 2 - btnSize / 2;
              return (
                <g
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveSlot(slot.key);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <rect
                    x={btnX}
                    y={btnY}
                    width={btnSize}
                    height={btnSize}
                    rx="6"
                    fill="#fff5f5"
                    stroke="#fca5a5"
                    strokeWidth="1"
                  />
                  {/* Close/cross icon (24 viewBox scaled into btnSize) */}
                  <g transform={`translate(${btnX},${btnY}) scale(${btnSize / 24})`}>
                    <path
                      stroke="#ef4444"
                      strokeWidth="3"
                      strokeLinecap="round"
                      fill="none"
                      d="M7 7 L17 17 M17 7 L7 17"
                    />
                  </g>
                </g>
              );
            })()}
          </g>
        );
      })}

      {/* Empty state */}
      {slots.length === 0 && (
        <text
          x={viewBox.width / 2}
          y={viewBox.height / 2}
          textAnchor="middle"
          fontSize="12"
          fill="#94a3b8"
          fontFamily="system-ui"
        >
          {emptyMessage || "Select a post type"}
        </text>
      )}

      <style>{`.ph{transition:fill .15s}.ph:hover{fill:rgba(59,130,246,0.06)}`}</style>
    </svg>
  );
};

export default InteractiveSignPost;
